'use client';

import type { FC } from 'react';
import { Badge, Spin, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useKmsDocumentStatus } from './hooks/useKmsDocumentStatus';

export interface KmsDocumentStatusBadgeProps {
  documentId: string;
  fileName: string;
  organizationId?: string | null;
  isSuperAdmin?: boolean;
}

export const KmsDocumentStatusBadge: FC<KmsDocumentStatusBadgeProps> = ({
  documentId,
  fileName,
  organizationId,
  isSuperAdmin = false,
}) => {
  const { t } = useTranslation('kms');
  const { data, isLoading, isError } = useKmsDocumentStatus(
    documentId,
    organizationId,
    isSuperAdmin,
  );

  if (isLoading && !data) {
    return (
      <span>
        <Spin size="small" /> {t('status.processing')}
      </span>
    );
  }

  if (isError || !data) {
    return <Tag color="error">{t('status.unknown')}</Tag>;
  }

  const status = data.status;
  if (status === 'processing') {
    const writing = data.extractedFields != null;
    return (
      <span>
        <Spin size="small" />{' '}
        {writing ? t('status.writing') : t('status.analyzing')}
      </span>
    );
  }
  if (status === 'linked') {
    return (
      <Badge
        status="success"
        text={
          data.assetId
            ? t('status.linkedToAsset', { assetId: data.assetId })
            : t('status.linked')
        }
      />
    );
  }
  if (status === 'pending_review') {
    return <Badge status="warning" text={t('status.awaitingReview')} />;
  }
  if (status === 'write_failed') {
    const errorText = data.amsWriteError?.trim();
    const label = t('status.writeFailed');
    return errorText ? (
      <Tooltip title={errorText}>
        <Badge status="error" text={label} />
      </Tooltip>
    ) : (
      <Badge status="error" text={label} />
    );
  }
  if (status === 'index_failed') {
    return <Badge status="error" text={t('status.failed')} />;
  }

  return (
    <Typography.Text type="secondary">
      {fileName}: {status}
    </Typography.Text>
  );
};
